import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Nfc } from 'lucide-react';
import type { CardLoginEventDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { CustomerProfileDrawer } from '@/features/customers/CustomerProfileDrawer';
import { useResource } from '@/features/settings/useSettings';
import { ApiError } from '@/lib/api';
import { AssignCardPanel } from './AssignCardPanel';
import { CardActivityTable } from './CardActivityTable';
import {
  CARD_LOGINS_PATH,
  nextPendingAssign,
  tapCard,
  type LoginFilter,
  type PendingAssignCard,
  type TapOutcome,
} from './cards';
import { TapResultCard } from './TapResultCard';
import { useCardReader } from './useCardReader';

/**
 * Cards — SPEC §5.7: RFID card management for staff.
 *
 * Three jobs on one screen, because they are one workflow at the counter:
 * tap a card to find whose it is; assign or remove a card; and watch the
 * venue-wide tap trail — where the unmatched cut is the orphan-card list and
 * the early warning that a reader or a card is failing.
 */
export function CardsPage() {
  const { t } = useTranslation();
  const { venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [armed, setArmed] = useState(false);
  const [outcome, setOutcome] = useState<TapOutcome | null>(null);
  const [tapError, setTapError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [pendingAssign, setPendingAssign] = useState<PendingAssignCard | null>(null);
  const [filter, setFilter] = useState<LoginFilter>('all');

  const activity = useResource<CardLoginEventDto[]>(CARD_LOGINS_PATH);

  async function handleRead(rawCardId: string): Promise<void> {
    setTapError(null);
    try {
      const result = await tapCard(rawCardId);
      setOutcome(result);
      // Matched or not, the server just wrote a CardLoginEvent — show it.
      void activity.reload();
    } catch (caught) {
      setOutcome(null);
      setTapError(caught instanceof ApiError ? caught.message : t('cards.tap.failed'));
    }
  }

  // Zero hold: the portal's hold guards against a card walking past a reader
  // in someone's pocket and starting a paid session. Here the tap is the
  // staff member's own deliberate act and only resolves a lookup.
  // With holdSeconds 0 the hook never reaches `lapsed` (a commit submits
  // immediately, and the tick's lapse branch requires a non-zero hold), so
  // this page renders no lapsed prompt or Cancel/Confirm UI — that block was
  // removed as unreachable. The hook itself stays portal-faithful.
  const reader = useCardReader({ enabled: armed, holdSeconds: 0, onRead: handleRead });

  const tapPrompt = !armed
    ? t('cards.tap.idlePrompt')
    : reader.phase === 'submitting'
      ? t('cards.tap.reading')
      : reader.phase === 'holding'
        ? t('cards.tap.holdPrompt')
        : t('cards.tap.armedPrompt');

  return (
    <div className="flex h-full flex-col overflow-auto p-4">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-content">{t('cards.title')}</h1>
        <p className="mt-1 text-sm text-content-muted">{t('cards.subtitle')}</p>
      </header>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <div className="space-y-4">
          <section
            aria-label={t('cards.tap.title')}
            className="rounded-card border border-subtle bg-raised p-4"
          >
            <h2 className="text-base font-semibold text-content">{t('cards.tap.title')}</h2>

            {/* Focus arms the reader; the wedge types into the window, and the
                hook ignores keystrokes aimed at real inputs, so the assign
                form below never swallows a card by accident. */}
            <div
              role="button"
              tabIndex={0}
              onFocus={() => setArmed(true)}
              onBlur={() => setArmed(false)}
              className={`mt-3 flex min-h-28 flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed p-4 text-center outline-none transition-colors ${
                armed
                  ? 'border-primary bg-secondary text-primary'
                  : 'border-subtle text-content-muted hover:border-strong'
              }`}
            >
              <Nfc className="h-7 w-7" aria-hidden />
              <p className="text-sm">{tapPrompt}</p>

              {reader.phase === 'holding' ? (
                <div className="h-1.5 w-40 overflow-hidden rounded-full bg-subtle">
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${Math.round(reader.progress * 100)}%` }}
                  />
                </div>
              ) : null}
            </div>

            {tapError ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                {tapError}
              </p>
            ) : null}
          </section>

          {outcome ? (
            <TapResultCard
              outcome={outcome}
              currencySymbol={currencySymbol}
              onOpenProfile={setProfileId}
              onAssign={(cardId) => {
                // Fresh nonce per tap: a repeat of the same card must
                // re-prefill the panel, not set identical state.
                setPendingAssign((current) => nextPendingAssign(current, cardId));
              }}
              onDismiss={() => setOutcome(null)}
            />
          ) : null}

          <AssignCardPanel
            pendingCard={pendingAssign}
            onChanged={(account) => {
              // The tap result that started this assignment is now stale:
              // either that card just matched this account, or it moved.
              setOutcome((current) =>
                current?.kind === 'unmatched' && current.cardId === account.cardId
                  ? { kind: 'matched', account }
                  : current,
              );
            }}
          />
        </div>

        <CardActivityTable
          events={activity.data}
          loading={activity.loading}
          error={activity.error}
          filter={filter}
          onFilterChange={setFilter}
          onRefresh={() => void activity.reload()}
        />
      </div>

      {profileId ? (
        <CustomerProfileDrawer
          customerId={profileId}
          currencySymbol={currencySymbol}
          onClose={() => setProfileId(null)}
          onChanged={() => void activity.reload()}
        />
      ) : null}
    </div>
  );
}
